'use client';

import { useState } from 'react';
import { AdminProfile, AdminUser } from '../../types';
import { MOCK_USERS } from '../../data/mock-data';
import AdminHeader from '../shared/AdminHeader';
import AdminNav from '../shared/AdminNav';

interface UsersPageProps {
  profile: AdminProfile;
  onNavigate: (page: 'dashboard' | 'receipts' | 'clients' | 'users' | 'settings') => void;
  onLogout: () => void;
  onGoToKiosk: () => void;
}

export default function UsersPage({
  profile,
  onNavigate,
  onLogout,
  onGoToKiosk,
}: UsersPageProps) {
  const [users, setUsers] = useState<AdminUser[]>(MOCK_USERS);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    full_name: '',
    phone: '',
    role: 'company_admin' as 'super_admin' | 'company_admin',
  });

  const [editForm, setEditForm] = useState({
    email: '',
    full_name: '',
    phone: '',
    role: 'company_admin' as 'super_admin' | 'company_admin',
  });

  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();

    if (newUser.password !== newUser.passwordConfirm) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (newUser.password.length < 8) {
      alert('비밀번호는 8자리 이상이어야 합니다.');
      return;
    }

    const newUserData: AdminUser = {
      id: `user-${Date.now()}`,
      user_id: `user-id-${Date.now()}`,
      company_id: 'mock-company-id',
      role: newUser.role,
      full_name: newUser.full_name,
      phone: newUser.phone,
      email: newUser.email,
      is_active: true,
      created_at: new Date().toISOString(),
      companies: { name: 'Demo Logistics Inc.' },
    };

    setUsers([...users, newUserData]);
    setShowAddModal(false);
    setNewUser({
      email: '',
      password: '',
      passwordConfirm: '',
      full_name: '',
      phone: '',
      role: 'company_admin',
    });
    alert('관리자가 추가되었습니다.');
  };

  const handleToggleActive = (userId: string) => {
    setUsers(
      users.map((u) =>
        u.user_id === userId ? { ...u, is_active: !u.is_active } : u
      )
    );
    alert('상태가 변경되었습니다.');
  };

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user);
    setEditForm({
      email: user.email || '',
      full_name: user.full_name,
      phone: user.phone,
      role: user.role,
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingUser) return;

    setUsers(
      users.map((u) =>
        u.user_id === editingUser.user_id
          ? { ...u, ...editForm }
          : u
      )
    );

    setShowEditModal(false);
    setEditingUser(null);
    alert('사용자 정보가 수정되었습니다.');
  };

  const handleDeleteUser = (userId: string, email: string) => {
    if (!confirm(`정말로 ${email} 계정을 삭제하시겠습니까?`)) {
      return;
    }

    setUsers(users.filter((u) => u.user_id !== userId));
    alert('계정이 삭제되었습니다.');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader profile={profile} onLogout={onLogout} onGoToKiosk={onGoToKiosk} />
      <AdminNav
        currentPage="users"
        onNavigate={onNavigate}
        isSuperAdmin={profile.role === 'super_admin'}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          {/* Add User Button */}
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              관리자 추가
            </button>
          </div>

          {/* Users Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이메일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이름
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연락처
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    역할
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    회사
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    생성일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.full_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.phone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          user.role === 'super_admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {user.role === 'super_admin' ? '최고 관리자' : '회사 관리자'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.companies?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.is_active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString('ko-KR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleToggleActive(user.user_id)}
                        className={`${
                          user.is_active
                            ? 'text-yellow-600 hover:text-yellow-900'
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        disabled={user.user_id === profile.user_id}
                      >
                        {user.is_active ? '비활성화' : '활성화'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.user_id, user.email || '')}
                        className="text-red-600 hover:text-red-900"
                        disabled={user.user_id === profile.user_id}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">관리자 정보 수정</h3>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">이메일</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                  placeholder="변경하지 않으려면 비워두세요"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">이름 *</label>
                <input
                  type="text"
                  required
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">연락처 *</label>
                <input
                  type="tel"
                  required
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="010-0000-0000"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">역할 *</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'super_admin' | 'company_admin' })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                  disabled={editingUser.user_id === profile.user_id}
                >
                  <option value="company_admin">회사 관리자</option>
                  <option value="super_admin">최고 관리자</option>
                </select>
                {editingUser.user_id === profile.user_id && (
                  <p className="mt-1 text-sm text-gray-500">자신의 역할은 변경할 수 없습니다.</p>
                )}
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  수정
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">새 관리자 추가</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">이메일 *</label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">비밀번호 *</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                  placeholder="8자리 이상"
                />
                <p className="mt-1 text-xs text-gray-500">비밀번호는 최소 8자리 이상이어야 합니다</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">비밀번호 확인 *</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newUser.passwordConfirm}
                  onChange={(e) => setNewUser({ ...newUser, passwordConfirm: e.target.value })}
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border ${
                    newUser.passwordConfirm && newUser.password !== newUser.passwordConfirm
                      ? 'border-red-500'
                      : ''
                  }`}
                  placeholder="비밀번호를 다시 입력하세요"
                />
                {newUser.passwordConfirm && newUser.password !== newUser.passwordConfirm && (
                  <p className="mt-1 text-xs text-red-500">비밀번호가 일치하지 않습니다</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">이름 *</label>
                <input
                  type="text"
                  required
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">연락처 *</label>
                <input
                  type="tel"
                  required
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="010-0000-0000"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">역할 *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'super_admin' | 'company_admin' })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2 border"
                >
                  <option value="company_admin">회사 관리자</option>
                  <option value="super_admin">최고 관리자</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  추가
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
